/**
 * Cross-cutting rules every shadow root picks up automatically: the shared button
 * styles (`.btn` with the `-primary`, `-outline`, `-soft`, `-ghost`, `-delete` and `-sm`
 * variants), the press-feedback nudge, the billing status badges (`.badge` with
 * `-red`, `-amber`, `-green`, `-gray`), `.low-margin`, `.visually-hidden`,
 * `.field-error` and the page section heading (`.section-head` with `.section-title` and `.section-subtitle`). Constructable stylesheets let the browser parse this once and adopt the same instance into every component's shadow root.
 * Adopted sheets come after a component's own `<style>`, so a component adjusts a
 * button only through a more specific selector (for example `.client-actions .btn`).
 */
const sharedInteractionStyles = new CSSStyleSheet();
sharedInteractionStyles.replaceSync(`
    .btn {
        padding: 0.5rem 1.25rem;
        min-height: 2.75rem;
        font-size: var(--text-sm);
        font-weight: var(--font-weight-semibold);
        border-radius: var(--radius-lg);
        cursor: pointer;
        transition: background-color 0.2s, border-color 0.2s, color 0.2s, box-shadow 0.2s, transform 0.06s;
        font-family: inherit;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        border: 1px solid transparent;
        line-height: 1;
        flex-shrink: 0;
        text-decoration: none;
    }
    .btn:focus-visible {
        outline: none;
        box-shadow: var(--ring-focus);
    }
    .btn:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
    .btn-primary {
        background: var(--color-accent);
        color: var(--color-accent-fg);
        box-shadow: var(--shadow-inset-press);
    }
    .btn-primary:not(:disabled):hover {
        background: var(--color-accent-hover);
    }
    .btn-outline {
        background: var(--color-surface);
        border-color: var(--color-gray-300);
        color: var(--color-gray-700);
    }
    .btn-outline:not(:disabled):hover {
        background: var(--color-bg);
        border-color: var(--color-gray-400);
    }
    .btn-soft {
        background: var(--color-accent-soft);
        border-color: var(--color-accent-soft-strong);
        color: var(--color-accent);
    }
    .btn-soft:not(:disabled):hover {
        background: var(--color-accent-soft-strong);
        border-color: var(--color-accent);
    }
    .btn-ghost {
        background: transparent;
        color: var(--color-text-muted);
    }
    .btn-ghost:not(:disabled):hover {
        background: var(--color-slate-100);
        color: var(--color-primary);
    }
    .btn-delete {
        background: var(--color-surface);
        border-color: var(--color-danger-border);
        color: var(--color-danger);
    }
    .btn-delete:not(:disabled):hover {
        background: var(--color-danger-soft);
        border-color: var(--color-danger);
    }
    .btn-sm {
        padding: 0.4rem 0.75rem;
        font-size: 0.8125rem;
    }
    .badge {
        font-size: 0.75rem;
        font-weight: 600;
        padding: 0.125rem 0.5rem;
        border-radius: 9999px;
    }
    .badge-red { background: var(--color-danger-soft); color: var(--color-danger-text); }
    .badge-amber { background: var(--color-warning-soft); color: var(--color-warning-text); }
    .badge-green { background: var(--color-success-soft); color: var(--color-success-text); }
    .badge-gray { background: var(--color-gray-100); color: var(--color-gray-600); }
    .low-margin {
        color: var(--color-warning-text);
        margin-right: 0.375rem;
    }
    .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }
    .field-error {
        margin: 0.25rem 0 0;
        color: var(--color-danger-text);
        font-size: 0.8125rem;
    }
    .section-head {
        margin: 0 0 1rem;
    }
    .section-title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin: 0;
        font-family: var(--font-display);
        font-weight: 600;
        letter-spacing: -0.01em;
        font-size: 1.25rem;
        color: var(--color-primary);
    }
    .section-title i {
        font-size: 1rem;
        color: var(--color-accent);
    }
    .section-subtitle {
        margin: 0.25rem 0 0;
        font-size: 0.875rem;
        color: var(--color-text-muted);
    }
    .btn:not(:disabled):active {
        transform: translateY(1px);
    }
    @media (prefers-reduced-motion: reduce) {
        .btn { transition: none; }
        .btn:active { transform: none; }
    }
`);

export class BaseComponent extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({mode: 'open'});
    }

    /**
     * Loads external HTML template and CSS into Shadow DOM. Also re-attaches the global
     * font and Font Awesome stylesheets into the shadow root — they're loaded once in
     * the light DOM via index.html, but CSS in a shadow tree doesn't inherit from
     * outside, so `<i class="fa-...">` icons and custom font-faces would otherwise
     * render as empty boxes / fallback serif. The browser cache makes the extra
     * <link>s practically free.
     */
    loadTemplate(htmlContent, cssContent) {
        const fontsLink = document.createElement('link');
        fontsLink.rel = 'stylesheet';
        fontsLink.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Geist:wght@400;500;600;700&family=Geist+Mono:wght@400;500&display=swap';

        const faLink = document.createElement('link');
        faLink.rel = 'stylesheet';
        faLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css';

        this.shadowRoot.innerHTML = `<style>${cssContent}</style>${htmlContent}`;
        this.shadowRoot.prepend(faLink);
        this.shadowRoot.prepend(fontsLink);
        this.shadowRoot.adoptedStyleSheets = [...this.shadowRoot.adoptedStyleSheets, sharedInteractionStyles];
    }

    $(selector) {
        return this.shadowRoot.querySelector(selector);
    }

    $$(selector) {
        return this.shadowRoot.querySelectorAll(selector);
    }

    emit(name, detail = {}) {
        this.dispatchEvent(new CustomEvent(name, {
            bubbles: true,
            composed: true,
            detail,
        }));
    }
}
